import styled, { css, keyframes } from 'styled-components'

import { DropdownButton, truncateLines } from '@sketch/components'
import { ReactComponent as PencilIcon } from '@sketch/icons/pencil-16'

// Shared Styles
import {
  SubtitleTextWrapper,
  Image,
  ImageElement,
  GridWrapper as GridWrapperBase,
  Name,
  DropdownButtonWrapper,
  SelectedCheckboxWrapper,
  SelectedCheckboxFloatingWrapper,
  StatusIcon,
} from '../../../shares/components/DocumentItem/DocumentItem.styles'

export const Container = styled.div`
  transition: opacity 0.2s ease-in-out;
  padding-bottom: 4px;

  /* Needed to prevent clipping the selection shadow */
  [data-being-dragged] {
    opacity: 0.3;
  }
`

export interface GridWrapperProps {
  $isSelected?: boolean
  $showSelectionCheckbox?: boolean
}

export const GridWrapper = styled(GridWrapperBase)<GridWrapperProps>(
  ({ theme, $isSelected, $showSelectionCheckbox }) => css`
    height: 72px;
    flex-direction: row;
    align-items: center;
    background: ${theme.colors.background.secondary.A};
    border-radius: 10px; /* stylelint-disable-line scales/radii */
    border: 4px solid ${theme.colors.background.secondary.A};

    ${SelectedCheckboxWrapper} {
      bottom: -4px;
      left: -4px;
    }

    ${Image} {
      border-radius: 0;

      &:first-child {
        &,
        & ${ImageElement} {
          border-radius: 6px 0 0 0;
        }
      }

      &:nth-child(3) {
        &,
        & ${ImageElement} {
          border-radius: 0 0 0 6px;
        }
      }

      &::after {
        content: none;
      }
    }

    ${Name} {
      line-height: 1;
      ${truncateLines(2)}
    }

    /* stylelint-disable-next-line no-descending-specificity */
    && ${/* sc-selector */ Name}, ${/* sc-selector */ SubtitleTextWrapper} {
      text-align: left;
    }

    ${($isSelected || $showSelectionCheckbox) &&
    css`
      && ${SelectedCheckboxFloatingWrapper} {
        visibility: visible;
        opacity: 1;
      }

      && ${Image} {
        opacity: 0.84;
      }
    `}
  `
)

export const GridWrapperLeft = styled.div(
  ({ theme }) => css`
    position: relative;
    display: flex;
    flex-shrink: 0;

    width: 64px;
    height: 64px;

    margin-right: 16px;
    background-color: ${theme.colors.background.tertiary.C};
    border-radius: 6px 0 0 6px;

    &::before,
    &::after {
      content: '';
      position: absolute;
      padding: 0;
      border-radius: 0;
      box-shadow: none;
      background: ${theme.colors.background.secondary.A};
    }

    &::before {
      width: 2px;
      height: 64px;
      top: 0;
      left: 31px;
    }

    &::after {
      width: 64px;
      height: 2px;
      top: 31px;
      left: 0;
    }
  `
)

export const GridWrapperRight = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 4px;
`

export const StyledName = styled(Name)`
  min-height: 17px;

  /* Needed to increase specificity */
  && {
    line-height: 1.2;
  }
`

export const StyledSubtitleTextWrapper = styled(SubtitleTextWrapper)`
  display: block;
  line-height: 1;

  b {
    display: block;
  }
`

export const InputWrapper = styled.div`
  position: relative;
`

export const StyledInput = styled.input`
  min-width: 100px;
  height: 100%;

  margin: -6px -6px -6px -2px; /* stylelint-disable-line scales/space */
  padding: 2px; /* stylelint-disable-line scales/space */
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSizes.E};

  background: none;
  outline: none;
  overflow: hidden;

  border: none;
  box-shadow: none;
  border-radius: ${({ theme }) => theme.radii.large};

  :focus {
    margin-left: -6px; /* stylelint-disable-line scales/space */
    padding-left: 4px;
    border: 2px solid ${({ theme }) => theme.colors.sketch.E};
  }

  ::selection {
    background: ${({ theme }) => theme.colors.sketch.E};
  }

  ::placeholder {
    color: ${({ theme }) => theme.colors.state.disabled};
  }
`

export const Pencil = styled(PencilIcon)`
  margin-left: 8px;
  width: 17px;

  /* This color should be "Light/colors.foreground.secondary.4" but given the icon composition */
  /* Opacity needs to be applied to the whole object */
  color: ${({ theme }) => theme.colors.foreground.secondary.A};
  opacity: 0.55;
`

export const GridNestedProjectItemOption = styled(DropdownButtonWrapper)`
  margin-left: auto;
  margin-right: 8px;

  position: relative;
  inset: 0;

  opacity: 0;
  transform: translate3d(0, -5px, 0);
  transition: ${({ theme }) => `
    opacity ${theme.transitions.duration[0]} ${theme.transitions.timing.easeInOut},
    transform ${theme.transitions.duration[0]} ${theme.transitions.timing.easeInOut};
  `};

  /* Prevent the button from hiding when the dropdown is open */
  [aria-expanded='true'] ${DropdownButton} {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
  &:has([aria-expanded='true']),
  :focus-within {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }

  @media (hover: none) {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`

const animation = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`

export const SelectedShadowWrapper = styled.div`
  position: absolute;
  inset: -8px;
  z-index: -1;
  animation: ${animation} 0.2s linear forwards;
`

export const SelectedShadow = styled.div`
  height: 100%;
  border-radius: 14px; /* stylelint-disable-line scales/radii */
  padding: 4px;
  background: linear-gradient(
    135deg,
    ${p => p.theme.colors.sketch.A},
    ${p => p.theme.colors.sketch.D}
  );
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
`

export const ProjectStatusIcon = styled(StatusIcon)`
  width: 17px;
  height: 17px;
`
